import { useRef, useState, useEffect } from "react";
import FormSeriesA from "./components/forms/FormSeriesA";
import FormSeriesB from "./components/forms/FormSeriesB";
import FormSeriesC from "./components/forms/FormSeriesC";
import FormSeriesD from "./components/forms/FormSeriesD";
import FormSeriesE from "./components/forms/FormSeriesE";
import FormSeriesF from "./components/forms/FormSeriesF";
import FormSeriesG from "./components/forms/FormSeriesG";
import DocumentSelector from "./components/inputs/DocumentSelector";

import { DocumentType } from "./DocumentTypes";
import { FeeDetails } from "./FeeCalculations";

import "./App.scss";

const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
});

const App: React.FC = () => {
    const [currentDocument, setCurrentDocument] = useState<DocumentType | null>(null);
    const [feeDetails, setFeeDetails] = useState<FeeDetails | null>(null);
    const documentTypeSelectorRef = useRef<HTMLSelectElement>(null);
    const feeDetailRef = useRef<HTMLHeadingElement>(null);

    const resetDocumentSelection = (): void => {
        setCurrentDocument(null);
        setFeeDetails(null);
        documentTypeSelectorRef?.current?.focus();
    };

    useEffect(() => {
        feeDetails != null && feeDetailRef?.current?.scrollIntoView({ behavior: "smooth" });
    }, [feeDetails]);

    const renderCurrentDocument = (): JSX.Element | null => {
        switch (currentDocument?.formSeries) {
            case "A":
                return (
                    <FormSeriesA
                        setFeeDetails={setFeeDetails}
                        resetDocumentSelection={resetDocumentSelection}
                        documentType={currentDocument}
                    />
                );
            case "B":
                return (
                    <FormSeriesB
                        setFeeDetails={setFeeDetails}
                        resetDocumentSelection={resetDocumentSelection}
                        documentType={currentDocument}
                    />
                );
            case "C":
                return (
                    <FormSeriesC
                        setFeeDetails={setFeeDetails}
                        resetDocumentSelection={resetDocumentSelection}
                        documentType={currentDocument}
                    />
                );
            case "D":
                return (
                    <FormSeriesD
                        setFeeDetails={setFeeDetails}
                        resetDocumentSelection={resetDocumentSelection}
                        documentType={currentDocument}
                    />
                );
            case "E":
                return (
                    <FormSeriesE
                        setFeeDetails={setFeeDetails}
                        resetDocumentSelection={resetDocumentSelection}
                        documentType={currentDocument}
                    />
                );
            case "F":
                return (
                    <FormSeriesF
                        setFeeDetails={setFeeDetails}
                        resetDocumentSelection={resetDocumentSelection}
                        documentType={currentDocument}
                    />
                );
            case "G":
                return (
                    <FormSeriesG
                        setFeeDetails={setFeeDetails}
                        resetDocumentSelection={resetDocumentSelection}
                        documentType={currentDocument}
                    />
                );
            default:
                return null;
        }
    };

    const handleSetCurrentDocument = (document: DocumentType | null): void => {
        setCurrentDocument(document);
        setFeeDetails(null);
    };

    return (
        <div className="recorder-fee-calc">
            <p>
                The following Fee Calculator may assist in assessing the cost of your needed service. Be sure to read
                through the steps and answer the questions. This calculator estimates the cost of service and additional
                fees may be applicable.
            </p>
            <DocumentSelector
                currentDocument={currentDocument}
                setCurrentDocument={handleSetCurrentDocument}
                documentTypeSelectorRef={documentTypeSelectorRef}
            />
            {currentDocument && (
                <>
                    <h2>{currentDocument.title}</h2>
                    <p>
                        The left-hand 2 ½ x 3 ½ inches of the space shall be used by the public to show the name of the
                        person requesting recording and the name and address to which the document is to be returned
                        following recording. The remaining right-hand side 2 ½ inches x 5 inches of space is to remain
                        blank for Recorder Use Only. In the event the first page or sheet of a document does not comply
                        with these requirements, a separate page shall be attached by the party requesting the recording
                        to the front of the document which meets these criteria and which reflects the title or titles
                        of the document as required by Section 27324 (
                        <a
                            rel="noopener noreferrer"
                            target="_blank"
                            href="https://leginfo.legislature.ca.gov/faces/codes_displaySection.xhtml?sectionNum=27361.6.&lawCode=GOV"
                        >
                            <abbr title="Government Code">GC</abbr>27361.6
                        </a>
                        ). The coversheet is considered an additional page for recording purposes.
                    </p>
                    <p>
                        Form fields marked with <i className="fa fa-asterisk" aria-hidden="true" title="Required"></i>
                        <span className="sr-only">Required</span> indicate a selection or input field is required.
                    </p>
                </>
            )}
            {renderCurrentDocument()}
            <div className="itemized-fees" aria-live="polite">
                {feeDetails != null && (
                    <>
                        <h2 ref={feeDetailRef} id="recording-fees">
                            Estimated Recording Fees - {currentDocument?.title}
                        </h2>
                        {typeof feeDetails.totalPages != "undefined" && <p>Total Pages: {feeDetails.totalPages}</p>}
                        <table>
                            <thead>
                                <tr>
                                    <th scope="col">Fee Description</th>
                                    <th scope="col">Fee</th>
                                </tr>
                            </thead>
                            <tbody>
                                {feeDetails.itemizedFees.map((fee, index) => (
                                    <tr key={`itemized_fee_${index}`}>
                                        <th scope="row">{fee.title}</th>
                                        <td>{currencyFormatter.format(fee.total)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tbody style={{ borderTop: "3px solid #AAA", fontSize: "1.5rem" }}>
                                <tr>
                                    <th scope="row">
                                        <strong>Total</strong>
                                    </th>
                                    <td>
                                        <strong>{currencyFormatter.format(feeDetails.totalFee)}</strong>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </>
                )}
            </div>
            <p>
                Checks are to be made payable to the Sonoma County Recorder. We will accept a Not To Exceed (
                <abbr title="Not To Exceed">NTE</abbr>) check. The <abbr title="Not To Exceed">NTE</abbr> amount will
                need to be written on the memo portion of the check. The space following the dollar sign and the line
                used for the written amount is to remain blank.
            </p>
            <p>
                <strong>Disclaimer:</strong> These calculations are estimates only based on your response. This list
                does not include all recordable document types. For questions regarding document recording, please call{" "}
                <a href="tel:+17075653800">(707) 565-3800</a>.
            </p>
        </div>
    );
};

export default App;
